import { motion } from "framer-motion";
import { ArrowDown, Heart } from "lucide-react";
import { maskLine } from "./motion";
import { ASSETS, openOrder } from "../../data/site";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const HEADLINE = ["Freshly", "Hand Rolled", "Premium"];

export const Hero = () => {
  return (
    <section id="hero" data-testid="hero-section"
      className="relative overflow-hidden pt-[84px] bg-[#FFF7F0]">
      {/* playful pastel blobs */}
      <div className="blob bg-[#FFD1E4] w-[420px] h-[420px] -top-24 -left-24 opacity-70" />
      <div className="blob bg-[#C8F5E9] w-[380px] h-[380px] top-40 -right-24 opacity-70" />
      <div className="blob bg-[#FFE9A8] w-[240px] h-[240px] bottom-[-40px] right-[46%] opacity-35" />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center py-14 sm:py-20 lg:py-24">
        {/* text */}
        <div className="relative z-10">
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[#12B99B] eyebrow shadow-[0_10px_24px_-14px_rgba(18,185,155,0.8)]">
            California's Hand Rolled Premium Ice Cream
          </motion.span>
          <motion.span initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F63C8B] px-4 py-2 text-white eyebrow ml-2 shadow-[0_10px_24px_-14px_rgba(246,60,139,0.9)]">
            <Heart size={13} className="fill-white" /> Proudly Women Owned
          </motion.span>

          <h1 className="font-display mt-6 leading-[0.95] text-[#2A1620] text-[clamp(2.6rem,7vw,5.5rem)]">
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={maskLine} initial="hidden" animate="show" className="block">{line}</motion.span>
              </span>
            ))}
            <span className="block overflow-hidden">
              <motion.span custom={3} variants={maskLine} initial="hidden" animate="show" className="block font-script text-[#F63C8B] text-[clamp(2.4rem,6.5vw,5rem)] leading-tight pb-2">
                Ice Cream
              </motion.span>
            </span>
          </h1>

          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 0.7 }}
            className="mt-6 max-w-lg text-[#6E5A64] text-base sm:text-lg leading-relaxed">
            Made fresh right before your eyes with premium ingredients — 25 signature rolled flavors,
            handcrafted shakes, complimentary toppings & unlimited drizzles.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3">
            <button data-testid="hero-view-menu" onClick={() => scrollTo("menu")}
              className="btn-primary px-8 py-4 text-sm font-bold uppercase tracking-[0.12em]">View Menu</button>
            <button data-testid="hero-find-location" onClick={() => scrollTo("locations")}
              className="btn-mint px-8 py-4 text-sm font-bold uppercase tracking-[0.12em]">Find a Location</button>
            <button onClick={openOrder} data-testid="hero-order-online"
              className="btn-outline text-center px-8 py-4 text-sm font-bold uppercase tracking-[0.12em]">Order Online</button>
          </motion.div>
        </div>

        {/* image */}
        <div className="relative z-10">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(246,60,139,0.55)] border-4 border-white">
            <img src={ASSETS.banner} alt="Assortment of hand rolled premium ice cream cups"
              decoding="async" className="block w-full h-[380px] sm:h-[520px] object-cover" />
          </motion.div>

          {/* $10 sticker */}
          <motion.div initial={{ opacity: 0, scale: 0.6, rotate: -18 }} animate={{ opacity: 1, scale: 1, rotate: -10 }} transition={{ delay: 0.9, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="absolute -top-5 -left-4 sm:-left-6 float-y bg-[#FFC93C] text-[#2A1620] rounded-full h-24 w-24 sm:h-28 sm:w-28 flex flex-col items-center justify-center shadow-[0_16px_30px_-12px_rgba(0,0,0,0.35)] border-4 border-white">
            <span className="font-display text-3xl sm:text-4xl leading-none">$10</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Every Cup</span>
          </motion.div>

          <img src={ASSETS.logoRed} alt="Rock N Roll badge"
            className="absolute -bottom-6 -right-4 h-24 w-24 sm:h-28 sm:w-28 object-contain animate-spin-slow drop-shadow-[0_10px_20px_rgba(246,60,139,0.35)] bg-white rounded-full border-4 border-white" />
        </div>
      </div>

      <div className="relative z-10 flex justify-center pb-8 text-[#B79AA6] text-xs uppercase tracking-[0.25em]">
        <span className="flex items-center gap-2">Scroll <ArrowDown size={15} className="animate-bounce" /></span>
      </div>
    </section>
  );
};
